import {rotuloDaSituacao, SITUACOES, SituacaoDoLead} from './leads-mia-gestor.component';
import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {HttpClient} from '@angular/common/http';

export interface LeadDoContato {
  id: number;
  telefone: string;
  nome: string;
  empresa: string;
  instagram: string | null;
  email: string | null;
  temComputador: boolean | null;
  origem: string;
  canal: string | null;
  ultimoFormularioEm: string;
  primeiraMensagemEm: string | null;
  testeCriadoEm: string | null;
  testeAcessadoEm: string | null;
  etapa: string;
  etapaMinima: string;
  situacaoConversa: SituacaoDoLead | null;
  /** por que foi marcado como Perdido; null fora de PERDIDO */
  motivoPerda: string | null;
}

export interface RespostaLeadContato {
  gestor: boolean;
  numero?: {id: number; telefone: string; modoTeste: boolean; ativadoEm: string | null};
  situacao?: 'telefone_invalido' | 'nao_lead' | 'antes_ativacao' | 'fora_lista' | 'atendendo';
  lead?: LeadDoContato | null;
  atendimento?: {
    id: number; fase: string; rotuloFase: string; encaminhamento: any; ultimaInteracao: string | null; jaFalou?: boolean;
    ficha: {segmento: string | null; ferramentaAtual: string | null; problema: string | null; objecao: string | null; proximoPasso: string | null};
    followup?: FollowupDoLead | null;
  } | null;
}

/** O que a Mia montou pela orientação da equipe (gestor-ia, sem gravar). */
export interface RascunhoDaMia {
  texto: string;
  botoes?: {id: string; titulo: string}[];
  lista?: any;
  link?: any;
}

/** Follow-up automático (D1, D3, D7) deste atendimento. */
export interface FollowupDoLead {
  ligadoNoNumero: boolean;
  ligado: boolean;
  proximo: {passo: number; agendadoPara: string} | null;
  ultimo: {passo: number; enviadoEm: string | null; respondido: boolean} | null;
}

const ROTULO_ETAPA: Record<string, string> = {NOVO: 'Novo', CONVERSA: 'Em conversa', TESTE: 'Teste criado', TESTE_ATIVO: 'Teste ativo', CLIENTE: 'Cliente', PERDIDO: 'Perdido'};
const ROTULO_ORIGEM: Record<string, string> = {site_meucardapio: 'MeuCardápio', site_cardapiodigital: 'CardápioDigital', saudacao_site: 'Saudação do site', manual: 'Manual'};

/**
 * Cartão do lead no painel do contato da extensão. Só aparece quando o número pessoal pareado é a
 * Mia Gestor; nesse caso o cartão da Mia da loja some (saída `gestorAtivo`).
 */
@Component({
  selector: 'app-lead-contato-gestor',
  templateUrl: './lead-contato-gestor.component.html',
  styleUrls: ['./lead-contato-gestor.component.scss'],
})
export class LeadContatoGestorComponent implements OnChanges {
  @Input() telefone: string;
  @Input() nome: string;
  @Output() gestorAtivo = new EventEmitter<boolean>();

  dados: RespostaLeadContato | null = null;
  carregando = false;
  erro = '';
  autorizando = false;
  chamando = false;
  iniciandoTeste = false;
  orientando = false;
  orientacao = '';
  /** prévia montada pela Mia; a equipe edita o texto e decide enviar */
  rascunho: RascunhoDaMia | null = null;
  enviando = false;
  movendo = false;
  mudandoFollowup = false;
  aviso = '';
  /** campo de motivo aberto (ao marcar Perdido ou ao editar o motivo de um lead já perdido) */
  anotandoPerda = false;
  motivoPerda = '';

  constructor(private http: HttpClient) {}

  ngOnChanges(mudancas: SimpleChanges) {
    if (!mudancas.telefone) return;
    // outro contato: nada do anterior pode ficar na tela enquanto a consulta volta
    this.dados = null; this.erro = ''; this.aviso = ''; this.anotandoPerda = false; this.motivoPerda = '';
    if (this.telefone) this.carregue();
  }

  get digitos(): string { return String(this.telefone || '').replace(/\D/g, ''); }

  get inicial(): string {
    const n = String(this.dados?.lead?.nome || this.nome || '').trim();
    return n ? n[0].toUpperCase() : 'L';
  }

  get telefoneFormatado(): string { return this.formateTelefone(this.digitos); }

  /** (62) 98104-3475 a partir do wa_id; sem DDI 55, devolve os dígitos como vieram */
  formateTelefone(telefone: string): string {
    const d = String(telefone || '').replace(/\D/g, '');
    const nacional = d.length >= 12 && d.startsWith('55') ? d.slice(2) : d;
    const m = nacional.match(/^(\d{2})(\d{4,5})(\d{4})$/);
    return m ? `(${m[1]}) ${m[2]}-${m[3]}` : d;
  }

  carregue() {
    if (!this.digitos) return;
    this.carregando = true; this.erro = '';
    this.http.get<RespostaLeadContato>(`/gestor-ia/lead-contato/${this.digitos}`).subscribe({
      next: r => {
        this.carregando = false;
        this.dados = r;
        this.gestorAtivo.emit(!!r.gestor);
      },
      error: e => {
        this.carregando = false;
        this.dados = null;
        this.erro = e?.error?.erro || 'Não foi possível consultar o lead.';
        this.gestorAtivo.emit(false);
      },
    });
  }

  get situacao() {
    const s = this.dados?.situacao;
    // Perdido vence a situação da conversa: a etapa do funil é o que a equipe precisa ver primeiro
    if (this.leadPerdido) {
      const motivo = String(this.dados.lead.motivoPerda || '').trim();
      return {tom: 'alerta', icone: 'fa-times-circle', titulo: 'Lead perdido', texto: `${motivo ? `Motivo: ${motivo}.` : 'Sem motivo anotado.'} A Mia não responde nem manda follow-up; reabra para ela voltar.`};
    }
    if (s === 'atendendo') return this.situacaoAtendendo();
    if (s === 'fora_lista') return {tom: 'atencao', icone: 'fa-flask', titulo: 'Fora da lista de teste', texto: 'É lead, mas o número está em modo teste e este telefone não está na lista.'};
    if (s === 'antes_ativacao') return {tom: 'atencao', icone: 'fa-history', titulo: 'Conversa anterior à ativação', texto: 'Este lead se registrou antes de a Mia assumir o número. Autorize para ela atender.'};
    if (s === 'telefone_invalido') return {tom: 'neutro', icone: 'fa-globe-americas', titulo: 'Sem telefone brasileiro', texto: 'A Mia Gestor só atende celulares brasileiros.'};
    return {tom: 'neutro', icone: 'fa-user', titulo: 'Não é lead', texto: 'Este contato não veio do site. Autorize para a Mia atender.'};
  }

  /**
   * "Mia Gestor atendendo" ganha o estado real da conversa (quem está devendo resposta e há quanto tempo),
   * porque é isso que decide o que a equipe faz agora. Sem situação da conversa, fica o texto genérico.
   */
  private situacaoAtendendo() {
    const sc = this.situacaoConversa;
    const ha = sc?.tempo ? ` há ${sc.tempo}` : '';
    const generico = {tom: 'ok', icone: 'fa-check', titulo: 'Mia Gestor atendendo', texto: 'Este lead está sendo atendido pela Mia no seu número.'};
    if (!sc) return generico;
    switch (sc.codigo) {
      case 'RESPONDENDO':
        return {tom: 'ok', icone: 'fa-comments', titulo: 'Conversa em andamento', texto: `O lead falou${ha}. A Mia responde por você no seu número.`};
      case 'AGUARDANDO_LEAD':
        return {tom: sc.minutos !== null && sc.minutos >= 60 ? 'atencao' : 'neutro', icone: 'fa-hourglass-half', titulo: `Lead sem responder${ha}`,
          texto: 'A Mia perguntou e ele ainda não voltou. Dá para chamar a atenção dele agora.'};
      case 'FOLLOWUP':
        return {tom: 'atencao', icone: 'fa-redo-alt', titulo: `${sc.rotulo} enviado${ha}`, texto: 'Toque automático sem resposta; o próximo já está agendado. Se quiser, chame a atenção dele antes.'};
      case 'ESFRIOU':
        return {tom: 'frio', icone: 'fa-snowflake', titulo: `Esfriou: sem resposta${ha}`, texto: 'Último toque sem resposta ou uma semana de silêncio. Não vira Perdido sozinho.'};
      case 'PRECISA_HUMANO':
        return {tom: 'alerta', icone: 'fa-hand-paper', titulo: 'Lead pediu a equipe', texto: `A Mia parou de responder${ha}. Responda você no WhatsApp ao lado ou oriente a Mia.`};
      case 'PEDIU_PARAR':
        return {tom: 'alerta', icone: 'fa-ban', titulo: 'Pediu para parar', texto: 'O lead pediu para não receber mais mensagens. A Mia não manda nada; respeite.'};
      default: return generico;
    }
  }

  /** Lead calado depois de a Mia falar: o botão principal vira "chamar a atenção", pelo caminho da retomada. */
  get leadCalado(): boolean {
    const c = this.situacaoConversa?.codigo;
    return this.miaJaFalou && (c === 'AGUARDANDO_LEAD' || c === 'FOLLOWUP' || c === 'ESFRIOU');
  }

  /** Pediu para parar: nada de chamar nem orientar; a tela não oferece o que a Mia vai recusar. */
  get pediuParar(): boolean { return this.situacaoConversa?.codigo === 'PEDIU_PARAR'; }

  /** Atalhos que preenchem a orientação; a equipe ajusta o texto se quiser e manda pelo mesmo botão. */
  get atalhosDeOrientacao(): string[] {
    const c = this.situacaoConversa?.codigo;
    if (c === 'AGUARDANDO_LEAD' || c === 'FOLLOWUP' || c === 'ESFRIOU') {
      return [
        'Pergunta, bem curto, se ficou alguma dúvida ou se ele quer que você mostre um exemplo pronto',
        'Oferece de novo o teste grátis de 7 dias, sem cobrar resposta, e pergunta se pode montar o cardápio hoje',
        'Manda uma mensagem leve perguntando se ainda faz sentido conversar agora ou se prefere outro horário',
      ];
    }
    if (c === 'PRECISA_HUMANO') return ['Avisa que a equipe viu e vai responder em instantes, sem tentar vender nada agora'];
    return [];
  }

  useAtalho(texto: string) { if (!this.orientando && !this.enviando && !this.chamando) this.orientacao = texto; }

  get podeAutorizar(): boolean {
    const s = this.dados?.situacao;
    return (s === 'nao_lead' || s === 'antes_ativacao') && !this.leadPerdido;
  }

  autorize() {
    if (this.autorizando || !this.digitos) return;
    this.autorizando = true; this.aviso = ''; this.erro = '';
    this.http.post<{lead: LeadDoContato; acordados: number}>(`/gestor-ia/lead-contato/${this.digitos}/autorizar`, {nome: this.nome || ''}).subscribe({
      next: r => {
        this.autorizando = false;
        this.aviso = r.acordados ? `Autorizado. ${r.acordados} mensagem(ns) recente(s) entregue(s) à Mia.` : 'Autorizado. A Mia responde a partir da próxima mensagem.';
        this.carregue();
      },
      error: e => { this.autorizando = false; this.erro = e?.error?.erro || 'Não foi possível autorizar.'; },
    });
  }

  /** Chamar a Mia: ela manda a primeira mensagem agora e passa a responder. Se já falou, vira retomada. */
  get podeChamar(): boolean {
    const s = this.dados?.situacao;
    return (s === 'nao_lead' || s === 'antes_ativacao' || s === 'atendendo') && !this.pediuParar && !this.leadPerdido;
  }

  /** Perdido: a Mia não atende (o Servidor recusa a mensagem e cancela toques); a tela só oferece reabrir. */
  get leadPerdido(): boolean { return this.dados?.lead?.etapa === 'PERDIDO'; }

  /** Volta ao degrau que os fatos provam (etapa mínima); a Mia passa a responder de novo. */
  reabra() { if (this.dados?.lead) this.mova(this.dados.lead.etapaMinima || 'CONVERSA'); }

  get miaJaFalou(): boolean {
    return this.dados?.situacao === 'atendendo' && !!this.dados?.atendimento?.jaFalou;
  }

  chameMia() {
    if (this.chamando || !this.digitos) return;
    this.chamando = true; this.aviso = ''; this.erro = '';
    this.http.post<{ok: boolean; retomada?: boolean; lead: LeadDoContato}>(`/gestor-ia/lead-contato/${this.digitos}/chamar-mia`, {nome: this.nome || ''}).subscribe({
      next: r => {
        this.chamando = false;
        this.aviso = r.retomada ? 'A Mia vai retomar a conversa em instantes, sem repetir o que já disse.'
          : 'A Mia entrou na conversa. A primeira mensagem dela sai em instantes, com o histórico como contexto.';
        setTimeout(() => this.carregue(), 4000);
      },
      error: e => { this.chamando = false; this.erro = e?.error?.erro || 'Não foi possível chamar a Mia.'; },
    });
  }

  /** Iniciar criação da loja: cabe enquanto a Mia ainda está qualificando (não em CRIAR_TESTE nem com teste criado). */
  get podeIniciarTeste(): boolean {
    const fase = this.dados?.atendimento?.fase;
    return this.podeChamar && !this.dados?.lead?.testeCriadoEm && fase !== 'CRIAR_TESTE' && fase !== 'AUTENTICADO';
  }

  /** A equipe decide pelo lead: a fase vira CRIAR_TESTE e a Mia pula o SPIN, pedindo só o que falta para o teste. */
  inicieTeste() {
    if (this.iniciandoTeste || !this.digitos) return;
    this.iniciandoTeste = true; this.aviso = ''; this.erro = '';
    this.http.post<{ok: boolean; lead: LeadDoContato}>(`/gestor-ia/lead-contato/${this.digitos}/iniciar-teste`, {nome: this.nome || ''}).subscribe({
      next: () => {
        this.iniciandoTeste = false;
        this.aviso = 'A Mia vai começar a criação do teste agora: ela pede só o que falta (loja, responsável, e-mail).';
        setTimeout(() => this.carregue(), 4000);
      },
      error: e => { this.iniciandoTeste = false; this.erro = e?.error?.erro || 'Não foi possível iniciar o teste.'; },
    });
  }

  /** Orientar a Mia: a equipe diz como quer a próxima mensagem e ela monta a prévia; nada sai ainda. */
  monteRascunho() {
    const orientacao = this.orientacao.trim();
    if (this.orientando || this.enviando || !this.digitos || !orientacao) return;
    this.orientando = true; this.aviso = ''; this.erro = '';
    this.http.post<{rascunho: RascunhoDaMia}>(`/gestor-ia/lead-contato/${this.digitos}/rascunho`, {orientacao}).subscribe({
      next: r => { this.orientando = false; this.rascunho = r.rascunho; },
      error: e => { this.orientando = false; this.erro = e?.error?.erro || 'Não foi possível montar a mensagem.'; },
    });
  }

  /** A equipe aprovou (talvez editou): sai pelo número da Mia e entra no histórico dela. */
  envieRascunho() {
    const rascunho = this.rascunho;
    if (!rascunho || this.enviando || this.orientando || !this.digitos || !rascunho.texto.trim()) return;
    this.enviando = true; this.aviso = ''; this.erro = '';
    this.http.post<{ok: boolean}>(`/gestor-ia/lead-contato/${this.digitos}/enviar-rascunho`,
      {texto: rascunho.texto.trim(), botoes: rascunho.botoes || [], lista: rascunho.lista || null, link: rascunho.link || null}).subscribe({
      next: () => {
        this.enviando = false; this.rascunho = null; this.orientacao = '';
        this.aviso = 'Mensagem enviada pela Mia. Ela continua a conversa dali.';
        setTimeout(() => this.carregue(), 4000);
      },
      error: e => { this.enviando = false; this.erro = e?.error?.erro || 'Não foi possível enviar a mensagem.'; },
    });
  }

  descarteRascunho() { if (!this.enviando && !this.orientando) this.rascunho = null; }

  mova(etapa: string, motivo = '') {
    if (this.movendo || !this.dados?.lead) return;
    this.movendo = true; this.aviso = ''; this.erro = '';
    const jaPerdido = this.dados.lead.etapa === 'PERDIDO';
    this.http.put<{lead: LeadDoContato}>(`/gestor-ia/lead-contato/${this.digitos}/etapa`, {etapa, motivo}).subscribe({
      next: r => {
        this.movendo = false; this.anotandoPerda = false;
        if (this.dados) this.dados.lead = r.lead;
        this.aviso = etapa === 'PERDIDO' && jaPerdido ? 'Motivo da perda anotado.' : `Movido para ${this.rotuloEtapa(etapa)}.`;
      },
      error: e => { this.movendo = false; this.erro = e?.error?.erro || 'Não foi possível mover.'; },
    });
  }

  /** Objeção que a Mia registrou na ficha comercial: vira sugestão de motivo, nunca marcação automática. */
  get objecaoDaFicha(): string { return String(this.dados?.atendimento?.ficha?.objecao || '').trim(); }

  /** Abre o campo de motivo, já sugerindo a objeção da ficha; marcar como Perdido só acontece ao confirmar. */
  abraPerda() {
    this.motivoPerda = this.dados?.lead?.motivoPerda || this.objecaoDaFicha;
    this.anotandoPerda = true; this.aviso = ''; this.erro = '';
  }

  get motivoSugerido(): boolean { return this.anotandoPerda && !this.dados?.lead?.motivoPerda && !!this.objecaoDaFicha && this.motivoPerda === this.objecaoDaFicha; }

  cancelePerda() { this.anotandoPerda = false; this.motivoPerda = ''; }

  confirmePerda() { this.mova('PERDIDO', this.motivoPerda.trim()); }

  get followup(): FollowupDoLead | null { return this.dados?.atendimento?.followup || null; }

  rotuloPasso(passo: number): string { return ['D1', 'D3', 'D7'][Number(passo) - 1] || `Toque ${passo}`; }

  /** Uma linha para o estado do follow-up; texto vazio quando não há atendimento. */
  get textoFollowup(): string {
    const f = this.followup;
    if (!f) return '';
    if (!f.ligadoNoNumero) return 'Desligado no número (tela de números da Mia Gestor).';
    if (!f.ligado) return 'Desligado para este lead.';
    if (f.proximo) return `Próximo toque ${this.rotuloPasso(f.proximo.passo)} em ${this.formateData(f.proximo.agendadoPara)}.`;
    if (f.ultimo && f.ultimo.respondido) return `Lead respondeu ao toque ${this.rotuloPasso(f.ultimo.passo)}.`;
    if (f.ultimo) return `Toque ${this.rotuloPasso(f.ultimo.passo)} enviado em ${this.formateData(f.ultimo.enviadoEm)}; ciclo encerrado.`;
    return 'Ligado. Agenda na próxima resposta da Mia na conversa comercial.';
  }

  formateData(valor: string | null): string {
    const d = valor ? new Date(valor) : null;
    if (!d || isNaN(d.getTime())) return '';
    const dois = (n: number) => String(n).padStart(2, '0');
    return `${dois(d.getDate())}/${dois(d.getMonth() + 1)} ${dois(d.getHours())}:${dois(d.getMinutes())}`;
  }

  mudeFollowup(ligado: boolean) {
    if (this.mudandoFollowup || !this.digitos) return;
    this.mudandoFollowup = true; this.aviso = ''; this.erro = '';
    this.http.put<{followup: FollowupDoLead}>(`/gestor-ia/lead-contato/${this.digitos}/followup`, {ligado}).subscribe({
      next: r => {
        this.mudandoFollowup = false;
        if (this.dados?.atendimento) this.dados.atendimento.followup = r.followup;
        this.aviso = ligado ? 'Follow-up ligado para este lead.' : 'Follow-up desligado para este lead; toques pendentes cancelados.';
      },
      error: e => { this.mudandoFollowup = false; this.erro = e?.error?.erro || 'Não foi possível alterar o follow-up.'; },
    });
  }

  rotuloEtapa(etapa: string): string { return ROTULO_ETAPA[etapa] || etapa; }

  /** Situação da conversa (quem está devendo resposta), com tom e dica; null fora de CONVERSA/TESTE. */
  get situacaoConversa() {
    const s = this.dados?.lead?.situacaoConversa;
    const def = s ? SITUACOES[s.codigo] : null;
    if (!s || !def) return null;
    const m = s.minutos;
    const tempo = m === null || m === undefined ? '' : m < 60 ? `${Math.max(0, m)} min` : m < 1440 ? `${Math.floor(m / 60)} h` : `${Math.floor(m / 1440)} d`;
    return {...def, codigo: s.codigo, minutos: m ?? null, rotulo: rotuloDaSituacao(s), tempo};
  }
  rotuloOrigem(origem: string): string { return ROTULO_ORIGEM[origem] || origem; }

  get linkKanban(): string { return `/admin/mia-gestor/leads?busca=${encodeURIComponent(this.dados?.lead?.telefone || this.digitos)}`; }

  get linkInstagram(): string | null {
    const i = this.dados?.lead?.instagram;
    if (!i) return null;
    const handle = i.replace(/^@/, '').trim();
    return /^[a-z0-9._]+$/i.test(handle) ? `https://instagram.com/${handle}` : null;
  }
}
